<script setup>
import moveFocus from '#helpers/dom/moveFocus.js'
import { autoUpdate, flip, shift, size, useFloating } from '@floating-ui/vue'
import { nanoid } from 'nanoid'
import { nextTick, ref, watch } from 'vue'
import { onClickOutside, useFocusWithin } from '@vueuse/core'
import { useRoute } from 'vue-router'

const props = defineProps({
	id: { type: String, default: () => nanoid(6) },
	btnClass: { type: String, default: 'btn-none' },
	disabled: { type: Boolean, default: false },
	position: { type: String, default: 'bottom-end' }
})
const route = useRoute()

/** @type {import('vue').Ref} */
const wrapperEl = ref(null)
/** @type {import('vue').Ref} */
const btnEl = ref(null)
/** @type {import('vue').Ref} */
const floatingEl = ref(null)
/** @type {import('vue').Ref} */
const menuEl = ref(null)
/** @type {import('vue').Ref} */
const showFloatingUI = ref(false)

const { floatingStyles } = useFloating(btnEl, floatingEl, {
	placement: props.position,
	middleware: [
		size({
			apply({ availableHeight }) {
				Object.assign(floatingEl.value.style, {
					// minWidth: `${rects.reference.width}px`,
					maxHeight: (availableHeight - 16) + 'px',
					minHeight: '2lh'
				})
			},
		}),
		shift(),
		flip()
	],
	whileElementsMounted: autoUpdate,
})
const { focused } = useFocusWithin(wrapperEl)

function openFloatingUI() { showFloatingUI.value = true }
function closeFloatingUI() { showFloatingUI.value = false }
function focusButton() { closeFloatingUI(); btnEl.value.focus() }

/**
 * @param {boolean} forward - Whether to move focus forward or backward
 * @returns {Promise<void>}
 */
async function moveArrow(forward = false) {
	if (!showFloatingUI.value) {
		showFloatingUI.value = true
		await nextTick()
	}
	moveFocus({
		forward,
		nextElements: Array.from(menuEl.value.querySelectorAll('a'))
	})
}

defineExpose({ closeFloatingUI })
onClickOutside(wrapperEl, () => { closeFloatingUI() })
watch(route, () => closeFloatingUI())
watch(focused, (focused) => {
	if (!wrapperEl.value.matches(':focus-within') && !focused) { closeFloatingUI() }
})
</script>

<template>
	<div
		ref="wrapperEl"
		class="position-relative"
	>
		<button
			:id="id + '_btn'"
			ref="btnEl"
			:aria-controls="id + '_menu'"
			:class="btnClass"
			:disabled="disabled"
			aria-haspopup="true"
			class="d-flex align-items-center gap-2 choice-btn"
			name="button"
			type="button"
			@click="openFloatingUI"
			@keydown.self.prevent.up="moveArrow(false)"
			@keydown.self.prevent.down="moveArrow(true)"
			@keydown.self.prevent.enter="moveArrow(true)"
		>
			<slot name="button">
				button
			</slot>
		</button>
		<div
			v-show="showFloatingUI"
			ref="floatingEl"
			:style="floatingStyles"
			class="floating-ui"
			role="group"
		>
			<menu
				:id="id + '_menu'"
				ref="menuEl"
				:aria-labelledby="id + '_btn'"
				class="choice-menu shadow-sharp"
				role="menu"
				@keydown.prevent.up="moveArrow(false)"
				@keydown.prevent.down="moveArrow(true)"
				@keydown.prevent.left="moveArrow(false)"
				@keydown.prevent.right="moveArrow(true)"
				@keydown.esc="focusButton"
			>
				<slot name="items">
					<li><a href="#">menu items</a></li>
				</slot>
			</menu>
		</div>
	</div>
</template>
